import { useState } from "react";
import { toast } from "sonner";
import { Calculator, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, Panel } from "@/components/FormField";
import { QuotePanel } from "@/components/portal/QuotePanel";
import { useBusiness } from "@/context/BusinessContext";
import { api, formatApiError } from "@/lib/api";
import { ui } from "@/lib/ui";

const EMPTY = { vehicle_make: "", vehicle_model: "", pickup_location: "", dropoff_location: "" };

export const RequestForm = ({ onCreated }) => {
  const { profile } = useBusiness();
  const [form, setForm] = useState(EMPTY);
  const [manual, setManual] = useState("");
  const [needManual, setNeedManual] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState("");

  const routePayload = () => ({
    pickup_location: form.pickup_location,
    dropoff_location: form.dropoff_location,
    ...(needManual && manual !== "" ? { manual_miles: Number(manual) } : {}),
  });

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (k.endsWith("location")) {
      setQuote(null);
    }
  };

  const calculate = async () => {
    setBusy("quote");
    try {
      setQuote((await api.post("/quote", routePayload())).data);
    } catch (err) {
      if (err?.response?.status === 422 && !needManual) {
        setNeedManual(true);
      }
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy("submit");
    try {
      const { data } = await api.post("/requests", { ...form, ...routePayload() });
      toast.success("Tow request submitted", { description: `Estimate ${data.towing_fee.toFixed(2)} USD · dispatch notified` });
      setForm(EMPTY);
      setManual("");
      setNeedManual(false);
      setQuote(null);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const field = (k, label, ph) => (
    <FormField id={k} label={label}>
      <Input id={k} required value={form[k]} onChange={set(k)} placeholder={ph} className={`h-11 ${ui.input}`}
        data-testid={`request-${k.replace("vehicle_", "car-").replace("_location", "-location").replace(/_/g, "-")}-input`} />
    </FormField>
  );

  const canQuote = form.pickup_location.trim().length >= 3 && form.dropoff_location.trim().length >= 3 && (!needManual || manual !== "");

  return (
    <form onSubmit={submit} data-testid="towing-service-request-form" className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
      <Panel className="p-5 sm:p-7 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          {field("vehicle_make", "Car make", "Toyota")}
          {field("vehicle_model", "Car model", "Camry")}
        </div>
        {field("pickup_location", "Pickup location", "Street, city, state")}
        {field("dropoff_location", "Drop-off location", "Street, city, state")}
        {needManual && (
          <FormField id="manual_miles" label="Estimated miles (route not found)">
            <Input id="manual_miles" type="number" min="0" step="0.1" required value={manual}
              onChange={(e) => { setManual(e.target.value); setQuote(null); }}
              className={`h-11 ${ui.input}`} data-testid="request-manual-miles-input" />
          </FormField>
        )}
        <Button type="button" onClick={calculate} disabled={!canQuote || !!busy} className={`${ui.btnSecondary} w-full h-11`} data-testid="calculate-quote-button">
          {busy === "quote" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}Calculate estimate
        </Button>
      </Panel>
      <div className="space-y-4">
        <QuotePanel quote={quote} baseCharge={profile?.base_charge} rate={profile?.per_mile_rate} />
        <Button type="submit" disabled={!quote || !!busy} className={`${ui.btnMagenta} w-full h-12`} data-testid="submit-tow-request-button">
          {busy === "submit" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Submit request
        </Button>
        {!quote && <p className="font-mono text-xs text-slate-500 text-center">Calculate your estimate to unlock submit.</p>}
      </div>
    </form>
  );
};
